import React, { useState, useEffect, useMemo } from "react";
import { collection, query, where, limit, getDocs, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import useCurrentUser from "../hooks/useCurrentUser";
import * as XLSX from "xlsx";
import {
    Search, Download, ArrowUpDown, Calendar, Filter,
    Activity, User, Package, Truck, FileText, ClipboardList,
    ShieldCheck, Hash, History
} from "lucide-react";

const TYPE_CONFIG = {
    inventory: { label: "Inventory",  color: "bg-blue-100 text-blue-700",    icon: Package,      dot: "bg-blue-500"    },
    order:     { label: "Order",      color: "bg-purple-100 text-purple-700", icon: ClipboardList, dot: "bg-purple-500"  },
    shipment:  { label: "Shipment",   color: "bg-amber-100 text-amber-700",   icon: Truck,        dot: "bg-amber-500"   },
    customs:   { label: "Customs",    color: "bg-teal-100 text-teal-700",     icon: FileText,     dot: "bg-teal-500"    },
    att:       { label: "ATT",        color: "bg-rose-100 text-rose-700",     icon: ShieldCheck,  dot: "bg-rose-500"    },
    system:    { label: "System",     color: "bg-slate-100 text-slate-600",   icon: Activity,     dot: "bg-slate-400"   },
};

function getConfig(type) {
    return TYPE_CONFIG[(type || "system").toLowerCase()] || TYPE_CONFIG.system;
}

function toSeconds(ts) {
    if (!ts) return 0;
    if (ts?.seconds) return ts.seconds;
    if (ts?.toDate) return Math.floor(ts.toDate().getTime() / 1000);
    const n = new Date(ts).getTime();
    return isNaN(n) ? 0 : Math.floor(n / 1000);
}

function formatTs(ts) {
    const s = toSeconds(ts);
    if (!s) return "—";
    return new Date(s * 1000).toLocaleString("en-AU", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit"
    });
}

export default function GeneralHistory() {
    const { clientId, uid } = useCurrentUser();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");
    const [filterType, setFilterType] = useState("ALL");

    useEffect(() => {
        if (!clientId) return;

        const fetchAll = async () => {
            setLoading(true);
            const allLogs = [];

            try {
                // ── 1. Clients/{clientId}/globalHistory (main log — logHistory() writes here) ──
                try {
                    const ghSnap = await getDocs(
                        collection(db, "Clients", clientId, "globalHistory")
                    );
                    ghSnap.docs.forEach(d => {
                        const v = d.data();
                        allLogs.push({
                            id: d.id,
                            type: v.type || "system",
                            action: v.action || "Event",
                            timestamp: v.timestamp,
                            reference: v.docId || "—",
                            details: v.note || (typeof v.details === "object" ? JSON.stringify(v.details) : v.details) || "—",
                            user: v.userId || v.role || "System",
                        });
                    });
                } catch (e) { console.warn("globalHistory:", e.message); }

                // ── 2. activityLogs root (logInventoryMovement() writes here) ─────────────
                try {
                    const alSnap = await getDocs(
                        query(collection(db, "activityLogs"), where("companyId", "==", clientId), limit(100))
                    );
                    alSnap.docs.forEach(d => {
                        const v = d.data();
                        allLogs.push({
                            id: `al_${d.id}`,
                            type: v.target || v.type || "inventory",
                            action: v.action || "Movement",
                            timestamp: v.timestamp,
                            reference: v.materialId || v.docId || "—",
                            details: v.note || `${v.materialNo || ""} ${v.qty ? `× ${v.qty}` : ""} ${v.location || ""}`.trim() || "—",
                            user: v.userId || v.role || "System",
                        });
                    });
                } catch (e) { console.warn("activityLogs:", e.message); }

                // ── 3. orders ─────────────────────────────────────────────────────────────
                try {
                    const ordSnap = await getDocs(
                        query(collection(db, "orders"), where("companyId", "==", clientId), limit(50))
                    );
                    ordSnap.docs.forEach(d => {
                        const v = d.data();
                        const itemCount = (v.items || []).length;
                        const total = (v.items || []).reduce((s, i) => s + (parseFloat(i.qty || 0) * parseFloat(i.price || 0)), 0);
                        allLogs.push({
                            id: `ord_${d.id}`,
                            type: "order",
                            action: `Order — ${v.status || "Unknown"}`,
                            timestamp: v.date || v.createdAt,
                            reference: v.orderNumber || d.id.slice(0, 8),
                            details: `${itemCount} item${itemCount !== 1 ? "s" : ""} · $${total.toLocaleString(undefined, { minimumFractionDigits: 2 })} · ${v.customer || ""}`,
                            user: v.createdBy || "Client",
                        });
                    });
                } catch (e) { console.warn("orders:", e.message); }

                // ── 4. shipments ──────────────────────────────────────────────────────────
                try {
                    const shSnap = await getDocs(
                        query(collection(db, "shipments"), where("companyId", "==", clientId), limit(50))
                    );
                    shSnap.docs.forEach(d => {
                        const v = d.data();
                        allLogs.push({
                            id: `sh_${d.id}`,
                            type: "shipment",
                            action: `Shipment — ${v.status || "Pending"}`,
                            timestamp: v.createdAt || v.etd,
                            reference: v.referenceNumber || d.id.slice(0, 8),
                            details: `${v.origin || "?"} → ${v.destination || "?"} · ${v.mode || ""} ${v.containerSize || ""}`.trim(),
                            user: "Client",
                        });
                    });
                } catch (e) { console.warn("shipments:", e.message); }

                // ── 5. customsEntries ─────────────────────────────────────────────────────
                try {
                    const ceSnap = await getDocs(
                        query(collection(db, "customsEntries"), where("companyId", "==", clientId), limit(50))
                    );
                    ceSnap.docs.forEach(d => {
                        const v = d.data();
                        allLogs.push({
                            id: `ce_${d.id}`,
                            type: "customs",
                            action: `Customs Entry — ${v.status || "Draft"}`,
                            timestamp: v.submittedAt || v.createdAt,
                            reference: v.declarationNumber || d.id.slice(0, 8),
                            details: v.description || v.portOfEntry || "—",
                            user: v.submittedBy || "Agent",
                        });
                    });
                } catch (e) { console.warn("customsEntries:", e.message); }

                // ── 6. attApplications ────────────────────────────────────────────────────
                try {
                    const attSnap = await getDocs(
                        query(collection(db, "attApplications"), where("companyId", "==", clientId), limit(30))
                    );
                    attSnap.docs.forEach(d => {
                        const v = d.data();
                        allLogs.push({
                            id: `att_${d.id}`,
                            type: "att",
                            action: `ATT Application — ${v.status || "In Progress"}`,
                            timestamp: v.createdAt,
                            reference: d.id.slice(0, 8),
                            details: `Step ${v.currentStep || 1} of ${v.totalSteps || "?"}`,
                            user: "Client",
                        });
                    });
                } catch (e) { console.warn("attApplications:", e.message); }

                // ── 7. inventories (current stock as snapshot events) ─────────────────────
                try {
                    const invSnap = await getDocs(
                        query(collection(db, "inventories"), where("companyId", "==", clientId), limit(50))
                    );
                    invSnap.docs.forEach(d => {
                        const v = d.data();
                        allLogs.push({
                            id: `inv_${d.id}`,
                            type: "inventory",
                            action: "SKU on record",
                            timestamp: v.createdAt,
                            reference: v.materialNo || d.id.slice(0, 8),
                            details: `${v.description || ""} · ${v.qty || 0} ${v.uom || "units"} · ${v.location || ""}`.trim(),
                            user: "System",
                        });
                    });
                } catch (e) { console.warn("inventories:", e.message); }

            } finally {
                // Deduplicate by id and sort
                const seen = new Set();
                const unique = allLogs.filter(l => { if (seen.has(l.id)) return false; seen.add(l.id); return true; });
                unique.sort((a, b) => toSeconds(b.timestamp) - toSeconds(a.timestamp));
                setLogs(unique);
                setLoading(false);
            }
        };

        fetchAll();
    }, [clientId]);

    const filteredLogs = useMemo(() => {
        return logs
            .filter(l => {
                if (filterType !== "ALL" && l.type?.toLowerCase() !== filterType.toLowerCase()) return false;
                const s = searchTerm.toLowerCase();
                return (
                    (l.action?.toLowerCase() || "").includes(s) ||
                    (l.reference?.toLowerCase() || "").includes(s) ||
                    (String(l.details)?.toLowerCase() || "").includes(s) ||
                    (l.user?.toLowerCase() || "").includes(s)
                );
            })
            .sort((a, b) => sortOrder === "asc"
                ? toSeconds(a.timestamp) - toSeconds(b.timestamp)
                : toSeconds(b.timestamp) - toSeconds(a.timestamp)
            );
    }, [logs, searchTerm, sortOrder, filterType]);

    const typeCounts = useMemo(() => {
        const counts = {};
        logs.forEach(l => { const t = l.type?.toLowerCase() || "system"; counts[t] = (counts[t] || 0) + 1; });
        return counts;
    }, [logs]);

    const exportToExcel = () => {
        const data = filteredLogs.map(l => ({
            Date: formatTs(l.timestamp),
            Type: l.type,
            "Action / Status": l.action,
            Reference: l.reference,
            Details: l.details,
            User: l.user,
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "General_History");
        XLSX.writeFile(wb, "General_History.xlsx");
    };

    return (
        <div className="max-w-7xl mx-auto pb-12">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif flex items-center gap-3">
                        <History size={28} className="text-gold-500" /> General History
                    </h1>
                    <p className="text-slate-500 mt-1">All transactions, stock movements, orders, and system events.</p>
                </div>
                <button
                    onClick={exportToExcel}
                    className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-bold flex items-center gap-2 transition-colors shadow-sm text-sm"
                >
                    <Download size={16} /> Export Excel
                </button>
            </div>

            {/* Summary chips */}
            {!loading && logs.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                    <button
                        onClick={() => setFilterType("ALL")}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${filterType === "ALL" ? "bg-navy-900 text-white border-navy-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                    >
                        All · {logs.length}
                    </button>
                    {Object.entries(typeCounts).map(([type, count]) => {
                        const cfg = getConfig(type);
                        const Icon = cfg.icon;
                        const isActive = filterType.toLowerCase() === type;
                        return (
                            <button
                                key={type}
                                onClick={() => setFilterType(isActive ? "ALL" : type)}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all flex items-center gap-1.5 ${isActive ? cfg.color + " border-current" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                            >
                                <Icon size={11} /> {cfg.label} · {count}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Toolbar */}
            <div className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm mb-6 flex flex-col md:flex-row gap-3 items-center">
                <div className="relative flex-1 w-full">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                    <input
                        type="text"
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-navy-900/10 text-sm placeholder:text-slate-400"
                        placeholder="Search by action, reference, or details..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <button
                    onClick={() => setSortOrder(p => p === "desc" ? "asc" : "desc")}
                    className="px-4 py-2.5 bg-slate-50 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-100 font-bold flex items-center gap-2 transition-colors text-sm shrink-0"
                >
                    <ArrowUpDown size={15} /> {sortOrder === "desc" ? "Newest first" : "Oldest first"}
                </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-16 text-center flex flex-col items-center gap-3 text-slate-400">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-900"></div>
                        <p className="text-sm">Loading history from all sources…</p>
                    </div>
                ) : filteredLogs.length === 0 ? (
                    <div className="p-16 text-center flex flex-col items-center gap-3">
                        <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center">
                            <History size={24} className="text-slate-400" />
                        </div>
                        <p className="font-bold text-slate-600">No records found</p>
                        <p className="text-sm text-slate-400">
                            {logs.length === 0
                                ? "Start by creating shipments, orders, or adding inventory."
                                : "Try adjusting your search or filter."}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Date & Time</th>
                                    <th className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Type</th>
                                    <th className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Action</th>
                                    <th className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Reference</th>
                                    <th className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Details</th>
                                    <th className="px-5 py-3.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">User</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {filteredLogs.map(log => {
                                    const cfg = getConfig(log.type);
                                    const Icon = cfg.icon;
                                    return (
                                        <tr key={log.id} className="hover:bg-slate-50/60 transition-colors group">
                                            <td className="px-5 py-3.5 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                                                    <span className="text-xs text-slate-500 font-medium">{formatTs(log.timestamp)}</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${cfg.color}`}>
                                                    <Icon size={11} /> {cfg.label}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3.5 whitespace-nowrap">
                                                <span className="text-sm font-semibold text-navy-900">{log.action}</span>
                                            </td>
                                            <td className="px-5 py-3.5 whitespace-nowrap">
                                                <span className="text-xs font-mono bg-slate-100 px-2 py-1 rounded-md text-slate-600">{log.reference || "—"}</span>
                                            </td>
                                            <td className="px-5 py-3.5 text-xs text-slate-500 max-w-xs truncate" title={log.details}>
                                                {log.details || "—"}
                                            </td>
                                            <td className="px-5 py-3.5 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                                    <User size={12} className="text-slate-400" />
                                                    {log.user || "System"}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 text-xs text-slate-400 text-right">
                            {filteredLogs.length} record{filteredLogs.length !== 1 ? "s" : ""}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
